import { create } from "zustand";

export interface CanvasDocumentWindowState {
  nodeId: string;
  offset: { x: number; y: number };
  size: { width: number; height: number };
  zIndex: number;
}

interface CanvasDocumentWindowStore {
  windows: CanvasDocumentWindowState[];
  open: (
    nodeId: string,
    anchorWidth: number,
    defaultSize: CanvasDocumentWindowState["size"],
  ) => void;
  close: (nodeId: string) => void;
  raise: (nodeId: string) => void;
  move: (nodeId: string, offset: CanvasDocumentWindowState["offset"]) => void;
  resize: (nodeId: string, size: CanvasDocumentWindowState["size"]) => void;
  reset: () => void;
}

function nextZIndex(windows: CanvasDocumentWindowState[]): number {
  return Math.max(0, ...windows.map((item) => item.zIndex)) + 1;
}

export const useCanvasDocumentWindowStore = create<CanvasDocumentWindowStore>((set) => ({
  windows: [],
  open: (nodeId, anchorWidth, defaultSize) => set((state) => {
    const zIndex = nextZIndex(state.windows);
    const existing = state.windows.find((item) => item.nodeId === nodeId);
    if (existing) {
      return {
        windows: state.windows.map((item) => item.nodeId === nodeId ? { ...item, zIndex } : item),
      };
    }
    return {
      windows: [...state.windows, {
        nodeId,
        offset: { x: (anchorWidth - defaultSize.width) / 2, y: -64 },
        size: { ...defaultSize },
        zIndex,
      }],
    };
  }),
  close: (nodeId) => set((state) => ({ windows: state.windows.filter((item) => item.nodeId !== nodeId) })),
  raise: (nodeId) => set((state) => {
    const active = state.windows.find((item) => item.nodeId === nodeId);
    if (!active || active.zIndex === Math.max(...state.windows.map((item) => item.zIndex))) return state;
    const zIndex = nextZIndex(state.windows);
    return { windows: state.windows.map((item) => item.nodeId === nodeId ? { ...item, zIndex } : item) };
  }),
  move: (nodeId, offset) => set((state) => ({
    windows: state.windows.map((item) => item.nodeId === nodeId ? { ...item, offset } : item),
  })),
  resize: (nodeId, size) => set((state) => ({
    windows: state.windows.map((item) => item.nodeId === nodeId ? { ...item, size } : item),
  })),
  reset: () => set({ windows: [] }),
}));
